/**
 * AbrirChamadoPage — formulário de abertura de chamado (B1.6 spec §5).
 *
 * Backend POST /api/v1/cliente/chamados é multipart:
 *   - titulo: string (min 3 chars)
 *   - descricao: string (min 5 chars)
 *   - foto: File opcional (≤10MB, image/*)
 *
 * Fluxo:
 *   1. User preenche título + descrição (+ opcional foto)
 *   2. Submit → POST multipart → backend cria deal Manutenção Corretiva
 *      + 2 comentários (interno + visível) + notif equipe Manutenção
 *   3. Resposta { id, numeroHumano } → navigate /cliente/chamado/:id
 *
 * Preview da foto antes do upload (UX). Validação client-side mínima
 * (tamanho + tipo); backend é fonte da verdade.
 *
 * NÃO REVERTER:
 *   - Multipart via FormData (cliente-api detecta automaticamente)
 *   - Validação tamanho 10MB SOMENTE client-side hint — backend é gatekeeper
 *   - Foto opcional (não required) — chamado sem foto continua válido
 *   - Após sucesso redirect imediato (UX — não fica na tela do form)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Button, Input, Label } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage } from '@/components/cliente/PortalPage';
import { AvisoDoPlantao } from '@/components/cliente/AvisoDoPlantao';
import { useCliente } from '@/contexts/ClienteContext';
import { useCreateChamado } from '@/lib/cliente-portal-store';
import { ClienteApiError } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

const MAX_FOTO_BYTES = 10 * 1024 * 1024;

export function AbrirChamadoPage() {
  const { clienteAtivo } = useCliente();
  const create = useCreateChamado();
  const navigate = useNavigate();
  const toast = useToast();
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function handleFotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    setErrorMsg(null);
    if (!file) {
      setFoto(null);
      setFotoPreview(null);
      return;
    }
    if (file.size > MAX_FOTO_BYTES) {
      setErrorMsg(
        `Essa foto tem ${(file.size / 1024 / 1024).toFixed(1)} MB e o limite é 10 MB. Tente tirar outra ou reduzir o tamanho.`,
      );
      e.target.value = '';
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrorMsg('Só dá para anexar fotos (JPG, PNG ou HEIC).');
      e.target.value = '';
      return;
    }
    setFoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (titulo.trim().length < 3) {
      setErrorMsg('Escreva um resumo com pelo menos 3 letras — ajuda a equipe a achar o chamado.');
      return;
    }
    if (descricao.trim().length < 5) {
      setErrorMsg('Conte um pouco mais sobre o que está acontecendo.');
      return;
    }
    try {
      const r = await create.mutateAsync({
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        foto,
      });
      // UX (Matheus 2026-05-28): redirect pra Meus chamados (não detail) +
      // toast confirmando — usuário vê o chamado recém-criado no topo da
      // lista com status atual da coluna do workflow Corretiva.
      const numero = r.chamado.numeroHumano;
      toast.show(
        numero != null
          ? `Chamado #${numero} aberto com sucesso! Nossa equipe foi notificada.`
          : 'Chamado aberto com sucesso! Nossa equipe foi notificada.',
        { variant: 'success' },
      );
      navigate('/cliente/meus-chamados');
    } catch (err) {
      if (err instanceof ClienteApiError && err.status === 400) {
        setErrorMsg('Confira o resumo e a descrição — faltou alguma informação.');
        return;
      }
      setErrorMsg(
        mensagemErro(err, 'Não conseguimos enviar seu chamado agora. Tente de novo em instantes.'),
      );
    }
  }

  return (
    <PortalPage
      testId="abrir-chamado-page"
      crumb="Meus chamados"
      title="Abrir chamado"
      subtitle={
        clienteAtivo
          ? `Conte o que está acontecendo em ${clienteAtivo.nome}. A equipe da IGM é avisada assim que você enviar.`
          : 'Conte o que está acontecendo. A equipe da IGM é avisada assim que você enviar.'
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface md:p-6"
      >
        <div>
          <Label htmlFor="titulo" required>
            Resumo do problema
          </Label>
          <Input
            id="titulo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: câmera da garagem sem imagem"
            maxLength={120}
            required
            disabled={create.isPending}
            data-testid="chamado-titulo-input"
          />
        </div>

        <div>
          <Label htmlFor="descricao" required>
            O que está acontecendo
          </Label>
          <textarea
            id="descricao"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Desde quando acontece, em que lugar, se já tentaram alguma coisa… quanto mais detalhe, mais rápido a equipe resolve."
            rows={6}
            maxLength={2000}
            required
            disabled={create.isPending}
            className="block w-full rounded-xl border border-line bg-paper-surface px-3 py-2 text-sm focus:border-red focus:outline-none focus:ring-2 focus:ring-red-soft dark:border-line-dark dark:bg-paper-dark-elevated"
            data-testid="chamado-descricao-input"
          />
          <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
            <span className="font-mono tabular-nums">{descricao.length}</span> de 2000 caracteres
          </p>
        </div>

        <div>
          <Label htmlFor="foto">Foto (opcional)</Label>
          <p className="mb-1.5 text-xs text-ink-2 dark:text-ink-dark-2">
            Uma foto do problema costuma resolver metade do chamado. JPG, PNG ou HEIC, até 10 MB.
          </p>
          <input
            id="foto"
            type="file"
            accept="image/*"
            onChange={handleFotoChange}
            disabled={create.isPending}
            className="block w-full text-sm file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-paper-warm file:px-4 file:py-2 file:text-sm file:font-medium file:text-ink-1 dark:file:bg-paper-dark-warm dark:file:text-ink-dark-1"
            data-testid="chamado-foto-input"
          />
          {fotoPreview && (
            <div className="mt-3" data-testid="chamado-foto-preview">
              <img
                src={fotoPreview}
                alt="Foto que você anexou ao chamado"
                className="max-h-48 rounded-lg border border-line dark:border-line-dark"
              />
            </div>
          )}
        </div>

        {/* D4: fora do horário, o aviso do plantão fica logo acima do botão de enviar. */}
        <AvisoDoPlantao />

        {errorMsg && (
          <Alert variant="danger" data-testid="chamado-error">
            {errorMsg}
          </Alert>
        )}

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            disabled={create.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="w-full sm:w-auto"
            disabled={create.isPending}
            data-testid="chamado-submit-btn"
          >
            {create.isPending ? 'Enviando…' : 'Enviar chamado'}
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}
